/**
 * Header Responsive Menu - Refactored Version
 *
 * Logic:
 * 1. Hoạt động trên màn hình desktop (>= 990px).
 * 2. Kiểm tra xem menu có bị tràn (overflow) - chiều rộng vượt quá container không.
 * 3. Nếu menu bị tràn, hiển thị hamburger icon. Menu vẫn HIỂN THỊ mặc định.
 * 4. User click hamburger icon để toggle ẩn/hiện menu.
 * 5. Tự động cập nhật khi resize window hoặc thay đổi breakpoint.
 * 6. Tự động dọn dẹp khi chuyển sang màn hình mobile hoặc khi section được tải lại.
 */
class HeaderResponsiveMenu {
  constructor() {
    // Ngăn chặn nhiều instance chạy cùng lúc
    if (window.headerMenuInstance) {
      window.headerMenuInstance.destroy();
    }

    this.header = document.querySelector('.header');
    this.inlineMenu = document.querySelector('.header__inline-menu');

    // Nếu không tìm thấy element cần thiết, dừng lại ngay
    if (!this.header || !this.inlineMenu) {
      return;
    }

    this.hamburgerToggle = null;
    this.isDestroyed = false;
    this.resizeTimeout = null;

    // Sử dụng matchMedia để theo dõi breakpoint một cách hiệu quả
    this.desktopMediaQuery = window.matchMedia('(min-width: 990px)');
    
    this.init();
    window.headerMenuInstance = this;
  }

  init() {
    this.createHamburger();
    this.addStyles();
    this.setupEventListeners();
    
    // Chạy kiểm tra lần đầu khi khởi tạo
    this.updateVisibility();
  }

  // Phương thức cốt lõi: kiểm tra và cập nhật trạng thái
  updateVisibility() {
    if (this.isDestroyed) return;

    // Chỉ hoạt động trên desktop
    if (this.desktopMediaQuery.matches) {
      // Kiểm tra xem menu có bị tràn (quá dài) không
      const isOverflowing = this.isMenuOverflowing();

      if (isOverflowing) {
        this.collapse();
      } else {
        this.reset();
      }
    } else {
      // Nếu không phải desktop, luôn reset về trạng thái mặc định
      this.reset();
    }
  }

  // Kiểm tra xem menu có bị tràn (quá dài) không
  isMenuOverflowing() {
    const header = this.header;
    const headerWidth = header.clientWidth; // Sử dụng clientWidth thay vì offsetWidth
    const headerStyle = window.getComputedStyle(header);
    const headerPaddingLeft = parseFloat(headerStyle.paddingLeft) || 0;
    const headerPaddingRight = parseFloat(headerStyle.paddingRight) || 0;

    // Chiều rộng khả dụng sau khi trừ padding
    const availableWidth = headerWidth - headerPaddingLeft - headerPaddingRight;

    // Tính tổng chiều rộng của tất cả các phần tử con trong header
    let totalChildrenWidth = 0;
    Array.from(header.children).forEach(child => {
      if (child !== this.hamburgerToggle && child.offsetParent !== null) {
        const style = window.getComputedStyle(child);
        const marginLeft = parseFloat(style.marginLeft) || 0;
        const marginRight = parseFloat(style.marginRight) || 0;
        totalChildrenWidth += child.offsetWidth + marginLeft + marginRight;
      }
    });

    // Thêm buffer 10px để tránh false positive
    return totalChildrenWidth > (availableWidth - 10);
  }

  // Kích hoạt trạng thái có hamburger icon (menu vẫn hiển thị mặc định)
  collapse() {
    this.header.classList.add('header--menu-collapsed');
    // Menu mặc định vẫn hiển thị, set aria-expanded = true
    // Đảm bảo menu được hiển thị
    this.inlineMenu.style.display = 'flex';
    if (this.hamburgerToggle) {
      this.hamburgerToggle.setAttribute('aria-expanded', 'true');
    }
  }

  // Trở về trạng thái mặc định (không có icon)
  reset() {
    this.header.classList.remove('header--menu-collapsed');
    this.header.classList.remove('header--menu-hidden');
    // Khi reset, xóa inline style để CSS gốc kiểm soát lại menu
    this.inlineMenu.style.display = '';
  }

  // Xử lý khi click vào icon hamburger
  toggleMenu() {
    // Kiểm tra xem menu có đang bị ẩn không (bằng class header--menu-hidden)
    const isHidden = this.header.classList.contains('header--menu-hidden');

    if (isHidden) {
      // Nếu đang ẩn, hiển thị menu
      this.header.classList.remove('header--menu-hidden');
      this.hamburgerToggle.setAttribute('aria-expanded', 'true');
    } else {
      // Nếu đang hiện, ẩn menu
      this.header.classList.add('header--menu-hidden');
      this.hamburgerToggle.setAttribute('aria-expanded', 'false');
    }
  }

  createHamburger() {
    this.hamburgerToggle = document.createElement('button');
    this.hamburgerToggle.className = 'header__desktop-hamburger';
    this.hamburgerToggle.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
        <path d="M2 4h16v2H2zM2 9h16v2H2zM2 14h16v2H2z"/>
      </svg>
    `;
    this.hamburgerToggle.setAttribute('aria-label', 'Toggle menu');
    this.hamburgerToggle.setAttribute('aria-expanded', 'false');

    const headerDrawer = this.header.querySelector('header-drawer');
    if (headerDrawer) {
      headerDrawer.parentNode.insertBefore(this.hamburgerToggle, headerDrawer);
    } else {
      this.header.querySelector('.header__icons')?.prepend(this.hamburgerToggle);
    }
    
    this.boundToggleMenu = this.toggleMenu.bind(this);
    this.hamburgerToggle.addEventListener('click', this.boundToggleMenu);
  }

  addStyles() {
    const styleId = 'header-responsive-menu-styles';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .header__desktop-hamburger {
        display: none;
        background: none; border: none;
        cursor: pointer; padding: 0.5rem;
        margin-right: 0.5rem;
        color: rgb(var(--color-foreground));
      }
      .header--menu-collapsed .header__desktop-hamburger {
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }
      /* Khi menu bị ẩn bởi toggle, thêm class này */
      .header--menu-hidden .header__inline-menu {
        display: none !important;
      }
    `;
    document.head.appendChild(style);
  }

  setupEventListeners() {
    this.boundUpdateVisibility = this.updateVisibility.bind(this);
    // Lắng nghe sự kiện 'change' của media query, hiệu quả hơn 'resize'
    this.desktopMediaQuery.addEventListener('change', this.boundUpdateVisibility);

    // Lắng nghe resize để kiểm tra overflow khi cửa sổ thay đổi kích thước
    this.boundResizeHandler = () => {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(this.boundUpdateVisibility, 100);
    };
    window.addEventListener('resize', this.boundResizeHandler);
  }

  destroy() {
    this.isDestroyed = true;

    // Clear any pending resize timeout
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = null;
    }

    this.desktopMediaQuery.removeEventListener('change', this.boundUpdateVisibility);

    if (this.boundResizeHandler) {
      window.removeEventListener('resize', this.boundResizeHandler);
    }

    if (this.hamburgerToggle) {
      this.hamburgerToggle.removeEventListener('click', this.boundToggleMenu);
      this.hamburgerToggle.remove();
    }

    this.header.classList.remove('header--menu-collapsed');
    this.header.classList.remove('header--menu-hidden');

    if (window.headerMenuInstance === this) {
      window.headerMenuInstance = null;
    }
  }
}

// --- Phần khởi tạo ---
function initHeaderMenu() {
  try {
    new HeaderResponsiveMenu();
  } catch (error) {
    console.error('Failed to initialize HeaderResponsiveMenu:', error);
  }
}

// Xử lý các kịch bản tải trang và Shopify section events
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHeaderMenu);
} else {
  initHeaderMenu();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('.header')) {
    setTimeout(initHeaderMenu, 100);
  }
});

window.addEventListener('beforeunload', () => {
  if (window.headerMenuInstance) {
    window.headerMenuInstance.destroy();
  }
});